/**
 * Mengenwahl: Portionen (oder Stück, Formen) mit Plus und Minus, eigene
 * Backform über die Fläche und die Wahl der Einheiten.
 */
import { BASE_UNIT_LABELS, UNIT_MODE_LABELS, formatDecimal, panLabel } from '@cookbook/domain';
import type { PanSize, UnitMode } from '@cookbook/domain';
import type { RecipeDetail } from '../api/types.ts';
import type { ScalingState } from '../state/useScaling.ts';
import { texts } from '../texts/de.ts';

/** Eigenschaften der Mengenwahl. */
export interface ScaleControlProps {
  readonly recipe: Pick<RecipeDetail, 'baseAmount' | 'baseUnit' | 'baseUnitDetail'>;
  readonly scaling: ScalingState;
}

/** Bezeichnung der Bezugsgröße, bei Formen mit Mehrzahl. */
export function yieldLabel(recipe: ScaleControlProps['recipe'], amount: number): string {
  if (recipe.baseUnit === 'pan') {
    return amount === 1 ? 'Form' : 'Formen';
  }
  return BASE_UNIT_LABELS[recipe.baseUnit];
}

/** Zahlenfeld für Formmaße. */
function SizeInput({
  label,
  value,
  onChange,
}: {
  readonly label: string;
  readonly value: number;
  readonly onChange: (value: number) => void;
}) {
  return (
    <label className="flex items-center gap-1">
      <span className="sr-only">{label}</span>
      <input
        type="number"
        min={8}
        max={80}
        value={value}
        aria-label={label}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (Number.isFinite(next) && next >= 8 && next <= 80) {
            onChange(next);
          }
        }}
        className="w-16 rounded-lg border border-border-subtle bg-surface px-2 py-1 text-center tabular-nums"
      />
    </label>
  );
}

/** Mengenwahl eines Rezepts. */
export function ScaleControl({ recipe, scaling }: ScaleControlProps) {
  const { amount, basePan, pan, unitMode, factor } = scaling;
  const currentPan: PanSize | null = pan ?? basePan;

  /** Wechselt die Form zwischen rund und eckig mit üblichen Maßen. */
  function changeShape(shape: PanSize['shape']) {
    if (currentPan === null || currentPan.shape === shape) {
      return;
    }
    if (shape === 'round') {
      scaling.setPan(basePan?.shape === 'round' ? basePan : { shape: 'round', diameter: 26 });
    } else {
      scaling.setPan(basePan?.shape === 'rect' ? basePan : { shape: 'rect', width: 20, length: 30 });
    }
  }

  return (
    <div className="space-y-3 rounded-xl bg-surface-sunken p-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center rounded-xl border border-border-subtle bg-surface-raised">
          <button
            type="button"
            onClick={() => scaling.setAmount(amount - 1)}
            disabled={amount <= 1}
            aria-label={texts.scale.less}
            className="h-10 w-10 text-lg disabled:opacity-40"
          >
            −
          </button>
          <span className="min-w-20 px-1 text-center font-semibold tabular-nums" aria-live="polite">
            {amount} {yieldLabel(recipe, amount)}
          </span>
          <button
            type="button"
            onClick={() => scaling.setAmount(amount + 1)}
            aria-label={texts.scale.more}
            className="h-10 w-10 text-lg"
          >
            +
          </button>
        </div>
        {factor === 1 ? null : (
          <span className="text-content-muted">
            {texts.scale.factor} × {formatDecimal(factor, 2)}
          </span>
        )}
        {scaling.changed ? (
          <button type="button" onClick={scaling.reset} className="text-xs text-accent underline">
            {texts.scale.reset}
          </button>
        ) : null}
      </div>

      {basePan === null || currentPan === null ? null : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{texts.scale.pan}</span>
          <select
            value={currentPan.shape}
            onChange={(event) => changeShape(event.target.value as PanSize['shape'])}
            className="rounded-lg border border-border-subtle bg-surface px-2 py-1"
          >
            <option value="round">{texts.scale.round}</option>
            <option value="rect">{texts.scale.rect}</option>
          </select>
          {currentPan.shape === 'round' ? (
            <>
              <span>Ø</span>
              <SizeInput
                label={texts.scale.diameter}
                value={currentPan.diameter}
                onChange={(diameter) => scaling.setPan({ shape: 'round', diameter })}
              />
              <span>cm</span>
            </>
          ) : (
            <>
              <SizeInput
                label={texts.scale.width}
                value={currentPan.width}
                onChange={(width) => scaling.setPan({ ...currentPan, width })}
              />
              <span>×</span>
              <SizeInput
                label={texts.scale.length}
                value={currentPan.length}
                onChange={(length) => scaling.setPan({ ...currentPan, length })}
              />
              <span>cm</span>
            </>
          )}
          <span className="w-full text-xs text-content-muted">
            {texts.scale.original}: {recipe.baseUnitDetail ?? panLabel(basePan)}
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label={texts.scale.units}>
        <span className="font-medium">{texts.scale.units}</span>
        {(Object.keys(UNIT_MODE_LABELS) as UnitMode[]).map((mode) => (
          <button
            key={mode}
            type="button"
            onClick={() => scaling.setUnitMode(mode)}
            aria-pressed={unitMode === mode}
            className={`rounded-full border px-3 py-1 text-xs ${
              unitMode === mode
                ? 'border-accent bg-accent-soft text-accent-strong'
                : 'border-border-subtle bg-surface-raised'
            }`}
          >
            {UNIT_MODE_LABELS[mode]}
          </button>
        ))}
      </div>
    </div>
  );
}
